interface WorkingHour {
  value: string | number;
  label: string;
}

interface AvailableSlot {
  value: number;
  label: string;
  available: boolean;
}

interface ApiResponse {
  date: string;
  product: string;
  availableSlots: AvailableSlot[];
}

interface OpenHourToolkitType {
  START_HOUR: string;
  END_HOUR: string;
  LAST_SLOT_VALUE: number;
  TEAM_WORKING_HOURS: WorkingHour[];
  CLOSED_DAYS: number[];
  formatHours: (startingHour: string, endingHour: string) => WorkingHour[];
  formatDateToYYYYMMDD: (date?: Date) => string;
  isToday: (date: string) => boolean;
  isHourAbove17: (date?: Date | string) => boolean;
  minSelectableSlotValue: () => number;
  minSelectableDate: () => string;
  nextOpenDates: (count: number) => string[];
  fetchAvailableSlots: (date: string, product?: string) => Promise<AvailableSlot[]>;
  getHoursForDay: (date: string, product?: string) => Promise<AvailableSlot[]>;
  getNextOpenDate: (date?: string, product?: string) => Promise<string>;
}

const OpenHourToolkit: OpenHourToolkitType = {
  START_HOUR: '1000',
  END_HOUR: '1800',
  // Last slot offered by the available-callback-slots API (17h30 - 18h00).
  LAST_SLOT_VALUE: 1730,

  TEAM_WORKING_HOURS: [
    {
      value: 1000,
      label: '10h00 - 10h30',
    },
    {
      value: 1030,
      label: '10h30 - 11h00',
    },
    {
      value: 1100,
      label: '11h00 - 11h30',
    },
    {
      value: 1130,
      label: '11h30 - 12h00',
    },
    {
      value: 1200,
      label: '12h00 - 12h30',
    },
    {
      value: 1230,
      label: '12h30 - 13h00',
    },
    {
      value: 1300,
      label: '13h00 - 13h30',
    },
    {
      value: 1330,
      label: '13h30 - 14h00',
    },
    {
      value: 1400,
      label: '14h00 - 14h30',
    },
    {
      value: 1430,
      label: '14h30 - 15h00',
    },
    {
      value: 1500,
      label: '15h00 - 15h30',
    },
    {
      value: 1530,
      label: '15h30 - 16h00',
    },
    {
      value: 1600,
      label: '16h00 - 16h30',
    },
    {
      value: 1630,
      label: '16h30 - 17h00',
    },
    {
      value: 1700,
      label: '17h00 - 17h30',
    },
    {
      value: 1730,
      label: '17h30 - 18h00',
    },
    {
      value: 1800,
      label: '18h00 - 18h30',
    },
  ],

  CLOSED_DAYS: [0, 6], // 0 = Sunday, 6 = Saturday

  formatHours: (startingHour: string, endingHour: string) => {
    const {TEAM_WORKING_HOURS} = OpenHourToolkit

    return TEAM_WORKING_HOURS.filter(({value}: { value: string | number }) => {
      // Convert both values to numbers for comparison if they're strings
      const valueNum = typeof value === 'string' ? parseInt(value) : value;
      const startNum = typeof startingHour === 'string' ? parseInt(startingHour) : startingHour;
      const endNum = typeof endingHour === 'string' ? parseInt(endingHour) : endingHour;
      
      return valueNum >= startNum && valueNum < endNum;
    });
  },

  formatDateToYYYYMMDD: (date = new Date()): string => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  },

  isToday: (date: string) => OpenHourToolkit.formatDateToYYYYMMDD() === date,

  isHourAbove17: (date = new Date()) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.getHours() + 1 > 17;
  },

  // Business rule: only offer slots at least 1h from now, rounded up to the next 30-min slot.
  minSelectableSlotValue: (): number => {
    const now = new Date();
    const minutes = now.getHours() * 60 + now.getMinutes() + 60;
    const rounded = Math.ceil(minutes / 30) * 30;
    return Math.floor(rounded / 60) * 100 + (rounded % 60);
  },

  minSelectableDate: (): string => {
    const date = new Date();
    if (OpenHourToolkit.minSelectableSlotValue() > OpenHourToolkit.LAST_SLOT_VALUE) {
      date.setDate(date.getDate() + 1);
    }
    while (OpenHourToolkit.CLOSED_DAYS.includes(date.getDay())) {
      date.setDate(date.getDate() + 1);
    }
    return OpenHourToolkit.formatDateToYYYYMMDD(date);
  },

  nextOpenDates: (count: number): string[] => {
    const [year, month, day] = OpenHourToolkit.minSelectableDate().split('-').map(Number);
    const date = new Date(year, month - 1, day, 12);
    const dates: string[] = [];
    while (dates.length < count) {
      if (!OpenHourToolkit.CLOSED_DAYS.includes(date.getDay())) {
        dates.push(OpenHourToolkit.formatDateToYYYYMMDD(date));
      }
      date.setDate(date.getDate() + 1);
    }
    return dates;
  },

  fetchAvailableSlots: async (date: string, product = 'loan'): Promise<AvailableSlot[]> => {
    try {
      const url = `${process.env.NEXT_PUBLIC_RM_BASE_API}/api/common/available-callback-slots?date=${encodeURIComponent(date)}&product=${product}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data: ApiResponse = await response.json();
      return data.availableSlots;
    } catch (error) {
      console.error('Error fetching available slots:', error);
      
      // Fallback to default slots
      return OpenHourToolkit.TEAM_WORKING_HOURS.map(slot => ({
        value: typeof slot.value === 'string' ? parseInt(slot.value) : slot.value,
        label: slot.label,
        available: true
      }));
    }
  },

  getHoursForDay: async (date: string, product = 'loan'): Promise<AvailableSlot[]> => {
    const availableSlots = await OpenHourToolkit.fetchAvailableSlots(date, product);
    
    // If it's today, keep only slots at least 1h away
    if (OpenHourToolkit.isToday(date)) {
      const minSlotValue = OpenHourToolkit.minSelectableSlotValue();

      return availableSlots.filter(slot => slot.value >= minSlotValue);
    }
    
    return availableSlots;
  },

  getNextOpenDate: async (date = OpenHourToolkit.formatDateToYYYYMMDD(), product = 'loan'): Promise<string> => {
    const currentDate = new Date(date);
    const day = currentDate.getDay();
    const time = new Date().getHours() + 1;
    const isToday = OpenHourToolkit.isToday(date);
    
    // Check if it's a closed day or after hours
    if (OpenHourToolkit.CLOSED_DAYS.includes(day) || (isToday && time > 17)) {
      const nextDate = new Date(date);
      nextDate.setDate(nextDate.getDate() + 1);
      return OpenHourToolkit.getNextOpenDate(OpenHourToolkit.formatDateToYYYYMMDD(nextDate), product);
    }
    
    // Check if there are any available slots for this date
    try {
      const availableSlots = await OpenHourToolkit.getHoursForDay(date, product);
      const hasAvailableSlots = availableSlots.some(slot => slot.available);
      
      if (!hasAvailableSlots) {
        // No available slots, try next day
        const nextDate = new Date(date);
        nextDate.setDate(nextDate.getDate() + 1);
        return OpenHourToolkit.getNextOpenDate(OpenHourToolkit.formatDateToYYYYMMDD(nextDate), product);
      }
      
      return date;
    } catch (error) {
      console.error('Error checking available slots:', error);
      // On error, return the current date as fallback
      return date;
    }
  },
}

export default OpenHourToolkit
